import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, getDocs, orderBy, limit, startAt, endAt } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Beaker } from 'lucide-react';
import { slugify, unslugify } from '../utils/slug';
import './SearchBar.css';

// Debounce delay: 300ms
const DEBOUNCE_DELAY = 300;
// Max suggestions to show
const MAX_SUGGESTIONS = 10;

export const SearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Search Firestore for ingredients matching the query
  const searchIngredients = useCallback(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Slug the search term to match how ingredients are stored in Firestore
      const searchSlug = slugify(searchTerm);
      
      // Query Firestore for ingredients that start with the slugged search term
      // Using startAt/endAt for prefix matching
      const ingredientsRef = collection(db, 'Ingredients');
      const q = query(
        ingredientsRef,
        orderBy('__name__'), // Order by document ID (ingredient name)
        startAt(searchSlug),
        endAt(searchSlug + '\uf8ff'), // \uf8ff is a high unicode character
        limit(MAX_SUGGESTIONS)
      );
      
      const snapshot = await getDocs(q);
      // Filter out any ingredient names containing '#' character
      const names = snapshot.docs
        .map(doc => doc.id)
        .filter(name => !name.includes('#'));
      
      console.log(`🔍 Firestore query for "${searchTerm}" (slugged: "${searchSlug}"): ${names.length} results`);
      
      setSuggestions(names);
      setShowSuggestions(names.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Error searching ingredients:', err);
      setError('Failed to search ingredients');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle clicks outside search bar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle input change with debouncing
  const handleInputChange = (value: string) => {
    setSearchQuery(value);

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If empty, clear suggestions immediately
    if (!value || value.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Set new debounce timer to query Firestore
    debounceTimerRef.current = setTimeout(() => {
      searchIngredients(value);
    }, DEBOUNCE_DELAY);
  };

  const handleSearch = (ingredientName: string) => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    // Ensure the ingredient name is slugged for the URL
    const sluggedName = slugify(ingredientName);
    navigate(`/${sluggedName}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      handleSearch(searchQuery.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      // Allow form submission even without suggestions
      if (e.key === 'Enter' && searchQuery.trim()) {
        return; // Let form submission handle it
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSearch(suggestions[selectedIndex]);
        } else if (searchQuery.trim()) {
          handleSearch(searchQuery.trim());
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    if (searchQuery && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="search-bar-container" ref={searchRef}>
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={isLoading ? "Loading ingredients..." : "Search ingredients..."}
            className="search-input"
            autoComplete="off"
            aria-label="Search for ingredients"
            aria-autocomplete="list"
            aria-controls="suggestions-list"
            aria-expanded={showSuggestions}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSuggestions([]);
                setShowSuggestions(false);
              }}
              className="clear-button"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
          <button 
            type="submit" 
            className="search-button" 
            aria-label="Search"
            disabled={!searchQuery.trim() || isLoading}
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </form>

      {error && (
        <div className="search-error" role="alert">
          {error}
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <ul 
          className="suggestions-list" 
          id="suggestions-list"
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSearch(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
              role="option"
              aria-selected={index === selectedIndex}
            >
              <Beaker className="suggestion-icon" size={16} />
              <span className="suggestion-text">{unslugify(suggestion)}</span>
            </li>
          ))}
        </ul>
      )}

      {showSuggestions && searchQuery && suggestions.length === 0 && !isLoading && (
        <div className="no-results">
          No ingredients found matching "{searchQuery}"
        </div>
      )}
    </div>
  );
};
